import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { getEvents, getCategories } from "../../services/eventService";
import imgPlaceholder from "../../assets/images/resources/img-placeholder.svg";
import { EVENT_IMAGE_POOL, pickPoolImage } from "../../utils/imagePools";

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const EventsMain = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [search, setSearch] = useState("");
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadEvents = useCallback((page = 1, category = "", q = "") => {
    setLoading(true);
    setError(null);
    getEvents({ page, category: category || undefined, q: q || undefined })
      .then((res) => {
        setEvents(res.data || []);
        setMeta(res.meta || { current_page: 1, last_page: 1 });
      })
      .catch(() => {
        setError(
          "Events could not be loaded. Please make sure the Laravel API server is running."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadEvents(1, "", "");
    getCategories()
      .then((res) => setCategories(res.data || []))
      .catch(() => {});
  }, [loadEvents]);

  const handleCategoryClick = (slug) => {
    setActiveCategory(slug);
    loadEvents(1, slug, search);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadEvents(1, activeCategory, search);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > meta.last_page) return;
    loadEvents(page, activeCategory, search);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <React.Fragment>
      {/* Events (Dynamic - from Laravel API) Start */}
      <section className="blog-page">
        <div className="container">
          {/* Filters */}
          <div className="row mb-4">
            <div className="col-xl-6 col-lg-6">
              <form onSubmit={handleSearchSubmit} className="d-flex" style={{ gap: "10px" }}>
                <input
                  type="text"
                  placeholder="Search events..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="form-control"
                  style={{ padding: "10px 15px", border: "1px solid #ddd", borderRadius: "6px" }}
                />
                <button type="submit" className="thm-btn">
                  Search
                </button>
              </form>
            </div>
            <div className="col-xl-6 col-lg-6">
              <ul className="list-unstyled d-flex flex-wrap" style={{ gap: "8px", marginTop: "10px" }}>
                <li>
                  <button
                    onClick={() => handleCategoryClick("")}
                    className="thm-btn"
                    style={{
                      opacity: activeCategory === "" ? 1 : 0.6,
                      padding: "6px 16px",
                      fontSize: "13px",
                    }}
                  >
                    All
                  </button>
                </li>
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <button
                      onClick={() => handleCategoryClick(cat.slug)}
                      className="thm-btn"
                      style={{
                        opacity: activeCategory === cat.slug ? 1 : 0.6,
                        padding: "6px 16px",
                        fontSize: "13px",
                      }}
                    >
                      {cat.name} ({cat.events_count})
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {loading && <p>Loading events...</p>}
          {error && <p style={{ color: "#c0392b" }}>{error}</p>}
          {!loading && !error && events.length === 0 && (
            <p>No published events found yet.</p>
          )}

          <div className="row">
            {events.map((event, index) => (
              <div
                key={event.id}
                className={`col-xl-4 col-lg-4 col-md-6 wow fadeIn${
                  index % 3 === 0 ? "Left" : index % 3 === 1 ? "Up" : "Right"
                }`}
                data-wow-delay={`${(index + 1) * 100}ms`}
              >
                <div className="blog-one__single">
                  <div className="blog-one__img">
                    <img
                      src={event.image || pickPoolImage(EVENT_IMAGE_POOL, event.id ?? event.slug ?? event.title) || imgPlaceholder}
                      alt={event.title}
                      onError={(e) => { e.target.onerror = null; e.target.src = imgPlaceholder; }}
                    />
                    <div className="blog-one__hover">
                      <Link to={`/events/${event.slug}`}>
                        <div className="blog-one__hover-icon-1">
                          <span className="blog-one__hover-icon-2"></span>
                        </div>
                      </Link>
                    </div>
                  </div>
                  <div className="blog-one__content">
                    <ul className="blog-one__meta list-unstyled">
                      <li>
                        <Link to="#">
                          <span className="icon-calendar"></span>{" "}
                          {formatDate(event.start_date)}
                        </Link>
                      </li>
                      {event.venue?.city && (
                        <li>
                          <Link to="#">
                            <span className="icon-pin"></span> {event.venue.city}
                          </Link>
                        </li>
                      )}
                    </ul>
                    <h3 className="blog-one__title">
                      <Link to={`/events/${event.slug}`}>{event.title}</Link>
                    </h3>
                    {event.price_from !== null && event.price_from !== undefined && (
                      <p style={{ margin: "6px 0", fontWeight: 600 }}>
                        From ${event.price_from}
                      </p>
                    )}
                    <div className="blog-one__btn-box-two">
                      <Link to={`/events/${event.slug}`} className="blog-one__btn-2 thm-btn">
                        View Details
                        <span className="icon-arrow-right"></span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {meta.last_page > 1 && (
            <div className="blog-list__pagination">
              <ul className="pg-pagination list-unstyled">
                {Array.from({ length: meta.last_page }, (_, i) => i + 1).map((p) => (
                  <li className="count" key={p}>
                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(p);
                      }}
                      className={p === meta.current_page ? "active-page" : ""}
                    >
                      {String(p).padStart(2, "0")}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
      {/* Events End */}
    </React.Fragment>
  );
};

export default EventsMain;
